import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, RotateCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatError } from "@/lib/api";
import { StatusBadge } from "@/pages/OrdersPage";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const TIMELINE = ["CONFIRMED", "PROCESSING", "SHIPPED", "OUT_FOR_DELIVERY", "DELIVERED"];

function ShipmentTimeline({ events }) {
  const done = new Set(events.map((e) => e.status));
  const current = events[events.length - 1];
  return (
    <div className="flex items-center gap-0 mt-4">
      {TIMELINE.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex flex-col items-center gap-1.5 min-w-0">
            {done.has(s) ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-200" />}
            <span className={`text-[9px] uppercase tracking-wide font-bold text-center ${done.has(s) ? "text-slate-900" : "text-slate-300"}`}>
              {s.replace(/_/g, " ")}
            </span>
          </div>
          {i < TIMELINE.length - 1 && <div className={`flex-1 h-0.5 mx-1 mb-5 ${done.has(TIMELINE[i + 1]) ? "bg-emerald-400" : "bg-slate-100"}`} />}
        </React.Fragment>
      ))}
      {current && <span className="sr-only">{current.status}</span>}
    </div>
  );
}

function ReturnDialog({ sellerOrderId, onDone }) {
  const { id } = useParams();
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);

  const submit = async () => {
    try {
      await api.post(`/orders/${id}/returns`, { seller_order_id: sellerOrderId, reason });
      toast.success("Return requested");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`return-btn-${sellerOrderId}`} variant="outline" size="sm" className="rounded-full"><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Return items</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Request a return</DialogTitle></DialogHeader>
        <Textarea data-testid="return-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you returning these items?" />
        <Button data-testid="return-submit-btn" onClick={submit} disabled={!reason.trim()} className="rounded-full bg-slate-900">Submit return request</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  const load = useCallback(() => {
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => toast.error("Order not found"));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!order) return <div className="max-w-5xl mx-auto px-8 mt-12"><div className="h-96 rounded-2xl bg-slate-100 animate-pulse" /></div>;

  const cancellable = order.seller_orders.some((so) => ["CONFIRMED", "PROCESSING"].includes(so.status));

  const cancel = async () => {
    try {
      await api.post(`/orders/${id}/cancel`, { reason: "Changed my mind" });
      toast.success("Order cancelled — mock refund issued");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const returnFor = (soId) => order.returns.find((r) => r.seller_order_id === soId);

  return (
    <div data-testid="order-detail-page" className="max-w-5xl mx-auto px-4 sm:px-8 mt-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-xs text-slate-400">ORDER #{order.id.slice(0, 8).toUpperCase()}</p>
          <h1 className="font-heading text-2xl font-bold mt-1 flex items-center gap-3">Order details <StatusBadge status={order.status} /></h1>
        </div>
        {cancellable && (
          <Button data-testid="cancel-order-btn" onClick={cancel} variant="outline" className="rounded-full text-red-500 border-red-200 hover:bg-red-50">
            <XCircle className="w-4 h-4 mr-2" /> Cancel order
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-[1fr_320px] gap-8 mt-8">
        <div className="space-y-6">
          {order.seller_orders.map((so) => (
            <div key={so.id} data-testid={`seller-order-${so.id}`} className="rounded-2xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between px-6 py-3 bg-slate-50">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Shipment · {so.seller_name}</p>
                <StatusBadge status={so.status} />
              </div>
              <div className="p-6">
                {!["CANCELLED", "REFUNDED"].includes(so.status) && <ShipmentTimeline events={so.shipment.events} />}
                <p className="text-xs text-slate-400 mt-3">Tracking ID: <span className="font-mono font-semibold text-slate-600">{so.shipment.tracking_id}</span> · {so.shipment.carrier}</p>
                <div className="mt-4 space-y-3">
                  {so.items.map((i) => (
                    <div key={i.offer_id} className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                        {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${i.product_id}`} className="text-sm font-semibold hover:text-blue-600 line-clamp-1">{i.title}</Link>
                        <p className="text-xs text-slate-400">Qty {i.qty}</p>
                      </div>
                      <span className="font-bold text-sm">{inr(i.price * i.qty)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-3">
                  {so.status === "DELIVERED" && !returnFor(so.id) && <ReturnDialog sellerOrderId={so.id} onDone={load} />}
                  {returnFor(so.id) && (
                    <p data-testid={`return-status-${so.id}`} className="text-xs font-semibold text-orange-600 inline-flex items-center gap-2">
                      Return {returnFor(so.id).status.replace(/_/g, " ").toLowerCase()} · {returnFor(so.id).reason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="rounded-2xl border border-slate-200 p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Order events</p>
            <div className="space-y-3">
              {[...order.events].reverse().map((e) => (
                <div key={e.id} className="flex gap-3 text-sm">
                  <span className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-xs">{e.status.replace(/_/g, " ")}</p>
                    {e.note && <p className="text-xs text-slate-500">{e.note}</p>}
                    <p className="text-[10px] text-slate-400">{new Date(e.at).toLocaleString("en-IN")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-6 h-fit">
          <div className="rounded-2xl border border-slate-200 p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Delivery address</p>
            <p className="text-sm font-semibold">{order.address.name}</p>
            <p className="text-sm text-slate-500 mt-1">{order.address.line1}, {order.address.city}, {order.address.state} {order.address.pincode}</p>
            <p className="text-sm text-slate-500">{order.address.phone}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Payment</p>
            <p className="text-sm font-semibold">{order.payment.method.replace(/_/g, " ")} · {order.payment.status}</p>
            <p className="text-xs text-slate-400 font-mono mt-1">{order.payment.txn_id}</p>
            <div className="border-t border-slate-100 mt-4 pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{inr(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>{order.shipping ? inr(order.shipping) : "Free"}</span></div>
              <div className="flex justify-between font-heading font-bold text-base pt-2 border-t border-slate-100"><span>Total</span><span>{inr(order.total)}</span></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
